import type Decimal from 'decimal.js';
import { ArrowRightLeft, Check, Pencil, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, DataTable, type DataTableColumn, NumberInput, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useBaseCurrency } from '../../lib/currenciesStore.js';
import { effectiveRateFor, formatRate, loadExchangeRates, saveExchangeRate, type RateRow } from '../../lib/exchangeRates.js';
import { formatDate, formatDateTime } from '../../lib/intl.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

/**
 * The currency-rates page — every rate ever set, and who set it when.
 *
 * The titlebar popover answers "what is the rate now"; this page answers the
 * follow-ups: what was it before, when exactly did it change (as_of is floored
 * to midnight by design, so the ENTERED-AT column carries the real clock time),
 * and by whom (created_by, recorded since the same release). Rates are
 * append-only, so this table IS the audit trail — nothing here edits history;
 * the only write is "set today's rate", same as everywhere else.
 */
export function ExchangeRatesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const canWrite = usePermission('exchange_rates.write');
  const base = useBaseCurrency();

  const [codes, setCodes] = useState<string[] | null>(null);
  const [rates, setRates] = useState<RateRow[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setRates(await loadExchangeRates());
  }, []);

  useEffect(() => {
    // Which currencies sit opposite the shop's own — so not before that is known.
    if (!base) return undefined;
    let on = true;
    void api().get<{ items: Array<{ code: string; isActive: boolean }> }>('/currencies')
      .then((r) => { if (on) setCodes(r.data.items.filter((c) => c.isActive && c.code !== base).map((c) => c.code)); })
      .catch(() => { if (on) setCodes([]); });
    void reload();
    return () => { on = false; };
  }, [base, reload]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const loading = !base || codes === null || rates === null;

  // Current-rate strip: pairs with history lead; bare activations only when
  // nothing has a rate yet. Same working-set rule as the titlebar popover.
  const shown = useMemo(() => {
    if (!codes || !rates) return [];
    const rated = codes.filter((c) => rates.some(
      (r) => (r.fromCurrency === c && r.toCurrency === base) || (r.fromCurrency === base && r.toCurrency === c),
    ));
    return (rated.length > 0 ? rated : codes).slice(0, 6);
  }, [codes, rates, base]);

  /**
   * Per-row delta vs the previous rate of the SAME stored direction. The list
   * arrives newest-first from the server, so "previous" is the next row down
   * with the same pair.
   */
  const deltas = useMemo(() => {
    const out = new Map<RateRow, Decimal | null>();
    if (!rates) return out;
    for (let i = 0; i < rates.length; i += 1) {
      const r = rates[i]!;
      let prev: RateRow | undefined;
      for (let j = i + 1; j < rates.length; j += 1) {
        const c = rates[j]!;
        if (c.fromCurrency === r.fromCurrency && c.toCurrency === r.toCurrency) { prev = c; break; }
      }
      out.set(r, prev ? safeDecimal(r.rate).minus(safeDecimal(prev.rate)) : null);
    }
    return out;
  }, [rates]);

  const save = async (code: string): Promise<void> => {
    const val = safeDecimal(normalizeNumericInput(draft));
    if (val.lte(0) || !base) return;
    setSaving(true);
    try {
      await saveExchangeRate(code, base, val.toDecimalPlaces(8).toString(), todayStr);
      setEditing(null);
      await reload();
      toast.success(t('dashboard.rateSaved', { defaultValue: 'Rate saved — used by everything posted from today on.' }));
    } catch {
      toast.error(t('dashboard.rateFailed', { defaultValue: "Couldn't save the rate." }));
    } finally {
      setSaving(false);
    }
  };

  const columns: DataTableColumn<RateRow>[] = [
    {
      id: 'asOf',
      header: t('fx.effectiveDate', { defaultValue: 'Effective from' }),
      sortValue: (r) => r.asOf,
      cell: (r) => formatDate(r.asOf, { locale: i18n.language }),
    },
    {
      id: 'pair',
      header: t('fx.pair', { defaultValue: 'Pair' }),
      sortValue: (r) => `${r.fromCurrency}${r.toCurrency}`,
      searchValue: (r) => `${r.fromCurrency} ${r.toCurrency}`,
      cell: (r) => <span className="font-mono">{r.fromCurrency} → {r.toCurrency}</span>,
    },
    {
      id: 'rate',
      header: t('fx.rate', { defaultValue: 'Rate' }),
      align: 'end',
      sortValue: (r) => Number(r.rate),
      cell: (r) => <span className="font-mono font-semibold">{formatRate(safeDecimal(r.rate), i18n.language)}</span>,
    },
    {
      id: 'change',
      header: t('fx.change', { defaultValue: 'Change' }),
      align: 'end',
      cell: (r) => {
        const d = deltas.get(r);
        if (!d || d.isZero()) return <span className="text-fg-subtle">—</span>;
        return (
          <span className={`inline-flex items-center gap-1 font-mono text-sm ${d.gt(0) ? 'text-success' : 'text-danger'}`}>
            {d.gt(0) ? <TrendingUp className="h-3.5 w-3.5" aria-hidden /> : <TrendingDown className="h-3.5 w-3.5" aria-hidden />}
            {formatRate(d.abs(), i18n.language)}
          </span>
        );
      },
    },
    {
      id: 'enteredAt',
      header: t('fx.enteredAt', { defaultValue: 'Entered at' }),
      sortValue: (r) => r.createdAt ?? '',
      cell: (r) => (r.createdAt
        ? formatDateTime(r.createdAt, { locale: i18n.language }, { dateStyle: 'medium', timeStyle: 'short' })
        : <span className="text-fg-subtle">—</span>),
    },
    {
      id: 'by',
      header: t('fx.by', { defaultValue: 'By' }),
      sortValue: (r) => r.createdByName ?? '',
      searchValue: (r) => r.createdByName ?? '',
      cell: (r) => r.createdByName ?? <span className="text-fg-subtle">—</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ArrowRightLeft className="h-6 w-6 text-brand" aria-hidden />
          {t('fx.title', { defaultValue: 'Currency rates' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('fx.subtitle', { defaultValue: 'Every rate ever set — what it was, when it changed, and who changed it. Rates are never overwritten: setting a new one adds a row, and old documents keep the rate of their day.' })}
        </p>
      </header>

      {/* Today's rate per pair, editable in place — same write as everywhere. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <Card className="p-4"><Skeleton className="h-9 w-full" /></Card>
        ) : shown.map((code) => {
          const current = base ? effectiveRateFor(rates, code, base, todayStr) : null;
          const isEditing = editing === code;
          return (
            <Card key={code} className="flex flex-wrap items-center gap-2 p-4">
              <span className="font-mono text-sm text-fg-muted">1 {code} =</span>
              {isEditing ? (
                <>
                  <div className="w-32">
                    <NumberInput
                      autoFocus
                      className="text-end font-mono"
                      value={draft}
                      onValueChange={setDraft}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); void save(code); }
                        if (e.key === 'Escape') setEditing(null);
                      }}
                      aria-label={t('fx.rate', { defaultValue: 'Rate' })}
                    />
                  </div>
                  <span className="text-sm text-fg-muted">{base}</span>
                  <Button type="button" size="sm" loading={saving} onClick={() => void save(code)} aria-label={t('actions.save')}>
                    <Check className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(null)} aria-label={t('actions.cancel')}>
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                </>
              ) : (
                <>
                  <span className="font-mono text-2xl font-bold">{current ? formatRate(current, i18n.language) : '—'}</span>
                  <span className="text-sm text-fg-muted">{base}</span>
                  {canWrite && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="ms-auto"
                      onClick={() => { setEditing(code); setDraft(current ? current.toDecimalPlaces(8).toString() : ''); }}
                      aria-label={t('dashboard.editRate', { defaultValue: 'Change the rate' })}
                      title={t('dashboard.editRate', { defaultValue: 'Change the rate' })}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="p-0">
        {loading ? (
          <div className="p-4"><Skeleton className="h-40 w-full" /></div>
        ) : (
          <DataTable
            columns={columns}
            data={rates}
            rowKey={(r) => `${r.fromCurrency}-${r.toCurrency}-${r.asOf}-${r.createdAt ?? ''}`}
            pageSize={25}
            searchPlaceholder={t('fx.searchPlaceholder', { defaultValue: 'Search by currency or person…' })}
          />
        )}
      </Card>
    </div>
  );
}
